// Stable per-owner camera device identity for daily Live. The id is random,
// stored locally per signed-in owner and never derived from the display name;
// the backend scopes it by owner. The name is display-only and truthful: the
// installed app platform, or the browser and OS family. Never a hostname.
const ID = /^[A-Za-z0-9_-]{16,64}$/;
const storageKey = ownerId => `katafit.camera.device.v1.${ownerId}`;
const memory = new Map(); // Storage unavailable: one id per owner for this page.

const nativePlatform = () => {
  try {
    const cap = typeof window === 'undefined' ? null : window.Capacitor;
    return cap?.isNativePlatform?.() ? cap.getPlatform?.() : null;
  } catch { return null; }
};

const BROWSERS = [['Microsoft Edge', 'Edge'], ['Opera', 'Opera'], ['Samsung Internet', 'Samsung Internet'], ['Google Chrome', 'Chrome'], ['Firefox', 'Firefox']];
const UA_BROWSERS = [[/Edg(A|iOS)?\//, 'Edge'], [/OPR\//, 'Opera'], [/SamsungBrowser\//, 'Samsung Internet'], [/Firefox\/|FxiOS\//, 'Firefox'],
  [/Chrome\/|CriOS\//, 'Chrome'], [/Safari\//, 'Safari']];
const UA_SYSTEMS = [[/Android/, 'Android'], [/iPhone/, 'iPhone'], [/iPad/, 'iPad'], [/CrOS/, 'ChromeOS'], [/Windows/, 'Windows'],
  [/Macintosh|Mac OS X/, 'Mac'], [/Linux/, 'Linux']];
const SYSTEMS = { macOS: 'Mac', 'Chrome OS': 'ChromeOS', 'Chromium OS': 'ChromeOS' };

export function cameraDeviceName({ platform = nativePlatform(), userAgentData = typeof navigator === 'undefined' ? null : navigator.userAgentData,
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent } = {}) {
  if (platform === 'android') return 'Android app';
  if (platform === 'ios') return 'iOS app';
  const brands = Array.isArray(userAgentData?.brands) ? userAgentData.brands.map(b => b?.brand) : [];
  const ua = typeof userAgent === 'string' ? userAgent : '';
  const browser = BROWSERS.find(([brand]) => brands.includes(brand))?.[1] || UA_BROWSERS.find(([pattern]) => pattern.test(ua))?.[1];
  const hinted = typeof userAgentData?.platform === 'string' && userAgentData.platform ? userAgentData.platform : null;
  const system = hinted ? SYSTEMS[hinted] || hinted : UA_SYSTEMS.find(([pattern]) => pattern.test(ua))?.[1];
  if (!browser) return 'Browser';
  return (system ? `${browser} on ${system}` : browser).slice(0, 60);
}

// The id is a non-secret slot key (authority is the signed-in owner); prefer
// Web Crypto, but never block Start where an older WebView lacks it.
function randomHex() {
  const crypto = typeof window === 'undefined' ? null : window.crypto;
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  if (typeof crypto?.getRandomValues === 'function') {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

export function cameraDeviceIdentity(ownerId, { storage = typeof window === 'undefined' ? null : window.localStorage,
  randomId = randomHex, ...naming } = {}) {
  let deviceId = null;
  try { deviceId = storage?.getItem(storageKey(ownerId)) ?? null; } catch { deviceId = null; }
  // Absent/unreadable storage falls back to this page's id; a malformed stored id is replaced.
  if (deviceId === null) deviceId = memory.get(ownerId) || null;
  if (!ID.test(deviceId || '')) {
    deviceId = `dev-${String(randomId()).replace(/[^A-Za-z0-9]/g, '')}`.slice(0, 64);
    memory.set(ownerId, deviceId);
    try { storage?.setItem(storageKey(ownerId), deviceId); } catch { /* In-memory identity for this page. */ }
  }
  return { deviceId, deviceName: cameraDeviceName(naming) };
}
